/**
 * <delivery-estimate> — Amazon-style "voraussichtliche Lieferung" line.
 *
 * All settings (transit days per region, cut-off hour, hide threshold, holidays)
 * come from data-attributes rendered by the block in Liquid. The country/region is
 * resolved server-side (Shopify localization); the block only renders for DE/AT/EU.
 * This script computes the dispatch day (Mon–Fri before cut-off), the estimated
 * delivery date (transit days, delivered Mon–Sat, skipping Sundays + holidays) and
 * the live "bei Bestellung innerhalb X" remaining time. Nothing is guaranteed —
 * wording stays "voraussichtlich".
 */

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.textEl = this.querySelector('[ref="text"]');
    if (!this.textEl) return;

    this.days = parseInt(this.dataset.days, 10) || 0;
    this.cutoffHour = clampInt(this.dataset.cutoff, 0, 23, 13);
    this.hideOverMs = (parseFloat(this.dataset.hideOver) || 20) * 3600 * 1000;
    this.tz = this.dataset.tz || 'Europe/Berlin';
    this.holidays = new Set((this.dataset.holidays || '').split(/[\s,;]+/).filter(Boolean));

    if (this.days <= 0) return;

    this.render();
    // Re-evaluate every minute so the remaining time and date stay correct.
    this.timer = setInterval(() => this.render(), 60 * 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /** Current wall-clock time in the shop timezone, as a naive-UTC Date for arithmetic. */
  now() {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: this.tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
    const p = {};
    for (const { type, value } of dtf.formatToParts(new Date())) p[type] = value;
    return new Date(Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second));
  }

  isoDay(d) {
    return d.toISOString().slice(0, 10);
  }

  /** Mon–Fri and not a holiday = a day we ship on. */
  isShippingDay(d) {
    const dow = d.getUTCDay();
    return dow >= 1 && dow <= 5 && !this.holidays.has(this.isoDay(d));
  }

  /** Mon–Sat and not a holiday = a day a parcel can be delivered on. */
  isDeliveryDay(d) {
    return d.getUTCDay() !== 0 && !this.holidays.has(this.isoDay(d));
  }

  render() {
    const now = this.now();
    const startOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const cutoffToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), this.cutoffHour));

    // Dispatch day: today if we still ship today, otherwise the next shipping day.
    let dispatch;
    if (this.isShippingDay(now) && now < cutoffToday) {
      dispatch = new Date(startOfToday);
    } else {
      dispatch = new Date(startOfToday);
      do {
        dispatch.setUTCDate(dispatch.getUTCDate() + 1);
      } while (!this.isShippingDay(dispatch));
    }

    // Delivery day: add transit days, counting only delivery days (Mon–Sat, no holidays).
    const delivery = new Date(dispatch);
    let added = 0;
    while (added < this.days) {
      delivery.setUTCDate(delivery.getUTCDate() + 1);
      if (this.isDeliveryDay(delivery)) added++;
    }

    const dateStr = `${WEEKDAYS[delivery.getUTCDay()]} ${delivery.getUTCDate()}. ${MONTHS[delivery.getUTCMonth()]}`;

    // Remaining time until the cut-off that pins this delivery date.
    const cutoff = new Date(Date.UTC(dispatch.getUTCFullYear(), dispatch.getUTCMonth(), dispatch.getUTCDate(), this.cutoffHour));
    const remaining = cutoff - now;

    let html = `Voraussichtliche Lieferung <strong class="delivery-estimate__date">${dateStr}</strong>`;
    if (remaining > 0 && remaining <= this.hideOverMs) {
      html += ` bei Bestellung innerhalb <strong class="delivery-estimate__within">${formatRemaining(remaining)}</strong>`;
    }
    html += '.';

    this.textEl.innerHTML = html;
    this.style.display = '';
  }
}

function clampInt(value, min, max, fallback) {
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function formatRemaining(ms) {
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h} Std ${m} min` : `${m} min`;
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
